import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useFavicon } from './lib/useBranding';
import { AppShell } from './components/shell/AppShell';
import { DashboardPage } from './pages/DashboardPage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import { KbPage } from './pages/KbPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProfilePage } from './pages/ProfilePage';
import { LoginPage } from './pages/LoginPage';
import { TicketsPage } from './pages/tickets/TicketsPage';
import { TicketDetailPage } from './pages/tickets/TicketDetailPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { EmailLogPage } from './pages/admin/EmailLogPage';
import { CannedResponsesPage } from './pages/admin/CannedResponsesPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { ChangeCalendarPage } from './pages/ChangeCalendarPage';
import { PortalApprovalsPage } from './pages/portal/PortalApprovalsPage';
import { SavedViewsPage } from './pages/admin/SavedViewsPage';
import { ApprovalsAdminPage } from './pages/admin/ApprovalsAdminPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { PersonPage } from './pages/PersonPage';
import { NewTicketPage } from './pages/tickets/NewTicketPage';
import { NewTaskPage } from './pages/tickets/NewTaskPage';
import { ReviewPrintPage } from './pages/tickets/ReviewPrintPage';
import { ApiKeysPage } from './pages/admin/ApiKeysPage';
import { FieldLibraryPage } from './pages/admin/FieldLibraryPage';
import { CatalogueAdminPage } from './pages/admin/CatalogueAdminPage';
import { CataloguePage } from './pages/CataloguePage';
import { ReportsPage } from './pages/ReportsPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminHomePage } from './pages/admin/AdminHomePage';
import { BusinessHoursPage } from './pages/admin/BusinessHoursPage';
import { SlaPoliciesPage } from './pages/admin/SlaPoliciesPage';
import { AutomationsPage } from './pages/admin/AutomationsPage';
import { DirectorySyncPage } from './pages/admin/DirectorySyncPage';
import { TicketTemplatesPage } from './pages/admin/TicketTemplatesPage';
import { MailSettingsPage } from './pages/admin/MailSettingsPage';
import { MailboxesPage } from './pages/admin/MailboxesPage';
import { BrandingPage } from './pages/admin/BrandingPage';
import { NotificationsPage } from './pages/admin/NotificationsPage';
import { RolesPage } from './pages/admin/RolesPage';
import { AgentsPage } from './pages/admin/AgentsPage';
import { ServiceDeskPage } from './pages/admin/ServiceDeskPage';
import { RequestersPage } from './pages/admin/RequestersPage';
import { SetupPage } from './pages/SetupPage';
import { FreezeWindowsPage } from './pages/admin/FreezeWindowsPage';
import { ApprovalBoardsPage } from './pages/admin/ApprovalBoardsPage';
import { SsoSettingsPage } from './pages/admin/SsoSettingsPage';
import { GroupsPage } from './pages/admin/GroupsPage';
import { RequesterGroupsPage } from './pages/admin/RequesterGroupsPage';
import { PortalLoginPage } from './pages/portal/PortalLoginPage';
import { PortalRedeemPage } from './pages/portal/PortalRedeemPage';
import { LoginRedeemPage } from './pages/LoginRedeemPage';
import { PortalHomePage } from './pages/portal/PortalHomePage';
import { PortalShell } from './pages/portal/PortalShell';
import { PortalCataloguePage } from './pages/portal/PortalCataloguePage';
import { PortalRequestPage } from './pages/portal/PortalRequestPage';
import { PortalTicketsPage } from './pages/portal/PortalTicketsPage';
import { PortalTicketPage } from './pages/portal/PortalTicketPage';
import { PortalReportIssuePage } from './pages/portal/PortalReportIssuePage';
import { PortalSearchPage } from './pages/portal/PortalSearchPage';
import { PortalProfilePage } from './pages/portal/PortalProfilePage';
import { PortalKbPage, PortalKbFolderPage, PortalKbArticlePage } from './pages/portal/PortalKbPage';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DirtyProvider } from './lib/dirty';
import { ConfirmProvider } from './lib/confirm';
import { queryClient } from './lib/queryClient';
import { Toaster } from 'sonner';

/** The portal's own gate. Any signed-in person may use the portal AS
 *  THEMSELVES — staff included (Step 88, Kai: "allowing an agent to go to
 *  that location, rather than redirecting"). Signed-out visitors still go to
 *  the portal's own door, never the agent one. Cosmetic by design; the API's
 *  wall + matrix are the control, and every portal route scopes to the
 *  caller's own id. */
function RequirePortal({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!me) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/portal/login?returnTo=${returnTo}`} replace />;
  }
  return <>{children}</>;
}

/** Client-side gate — cosmetic by design; the API's wall+matrix are the control. */
function RequireSignIn({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!me) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }
  // A requester session never sees the agent shell — with zero agent
  // permissions it renders as an empty sidebar over 403s, which reads as
  // breakage (it read exactly that way to Kai). Their home is the portal.
  if (me.user.kind === 'requester') return <Navigate to="/portal" replace />;
  // Step 165 — an admin on an unfinished install is guided to the wizard from
  // wherever they landed. Only admins: an agent just sees the desk.
  if (me.setupRequired && !location.pathname.startsWith('/setup')) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

/** Providers that need router hooks (DirtyProvider uses useBlocker) live INSIDE
 *  the router as a layout route; providers that don't, wrap it outside. */
function RootLayout() {
  return (
    <DirtyProvider>
      {/* Inside DirtyProvider so a confirm can sit over the unsaved-changes
          dialog if one is somehow already up — and inside the router, because
          pages call it. */}
      <ConfirmProvider>
        <Outlet />
      </ConfirmProvider>
    </DirtyProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      // Step 155 — where an emailed agent sign-in link lands.
      { path: '/login/redeem', element: <LoginRedeemPage /> },
      /**
       * Step 54 — the stakeholder copy, OUTSIDE the app shell.
       *
       * It is a document, not a page in the product. Inside the shell it
       * rendered with the sidebar and top bar around it, and — worse — those
       * would have printed, because `@media print` only hides what it is told
       * to and the chrome is not part of this component. A standalone route
       * has nothing to hide.
       */
      { path: '/tickets/:id/review/print', element: <ReviewPrintPage /> },
      // The requester domain: its own door, its own landing — never the agent's.
      { path: '/portal/login', element: <PortalLoginPage /> },
      /**
       * Step 171 — the separate address Kai asked for. Same page, Microsoft
       * suppressed, so a requester without a Microsoft account has somewhere
       * to be sent that is not the front door they cannot use.
       */
      { path: '/portal/login/email', element: <PortalLoginPage emailOnly /> },
      { path: '/portal/redeem', element: <PortalRedeemPage /> },
      {
        path: '/portal',
        element: (
          <RequirePortal>
            <PortalShell />
          </RequirePortal>
        ),
        children: [
          { index: true, element: <PortalHomePage /> },
          { path: 'search', element: <PortalSearchPage /> },
          { path: 'profile', element: <PortalProfilePage /> },
          { path: 'help', element: <PortalKbPage /> },
          { path: 'help/folder/:folderId', element: <PortalKbFolderPage /> },
          { path: 'help/:slug', element: <PortalKbArticlePage /> },
          { path: 'catalogue', element: <PortalCataloguePage /> },
          { path: 'report', element: <PortalReportIssuePage /> },
          // Step 47 — a requester-domain approvals surface, because the
          // person signing off a request usually has no agent licence.
          { path: 'approvals', element: <PortalApprovalsPage /> },
          { path: 'catalogue/:id', element: <PortalRequestPage /> },
          { path: 'requests', element: <PortalTicketsPage /> },
          { path: 'requests/:id', element: <PortalTicketPage /> },
        ],
      },
      // Step 165 — the setup wizard has its own full-page frame, not the
      // shell: on a fresh install there is nothing in the shell to show yet.
      {
        path: '/setup',
        element: (
          <RequireSignIn>
            <SetupPage />
          </RequireSignIn>
        ),
      },
      {
        element: (
          <RequireSignIn>
            <AppShell />
          </RequireSignIn>
        ),
        children: [
          { path: '/', element: <Navigate to="/dashboard" replace /> },
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/search', element: <SearchResultsPage /> },
          { path: '/tickets', element: <TicketsPage /> },
          // Create is a SUBPAGE, not a popup (Kai). Static segments outrank
          // `:id` in the router's own ranking, so `/tickets/new` cannot be
          // swallowed by the detail route.
          { path: '/tickets/new', element: <NewTicketPage /> },
          { path: '/tasks/new', element: <NewTaskPage /> },
          { path: '/tickets/:id', element: <TicketDetailPage /> },
          // Step 45 — each tab is a URL SEGMENT, not `useState`, so a
          // colleague can be sent straight to the tab that matters. One
          // element for all of them: the page reads `tab` and switches its
          // body, keeping the properties rail mounted across the lot.
          { path: '/tickets/:id/:tab', element: <TicketDetailPage /> },
          {
            path: '/admin',
            element: <AdminLayout />,
            children: [
              // Step 93: /admin is now the settings INDEX, not a redirect to
              // whichever page happened to be first.
              { index: true, element: <AdminHomePage /> },
              { path: 'mail', element: <MailSettingsPage /> },
              { path: 'mailboxes', element: <MailboxesPage /> },
              { path: 'branding', element: <BrandingPage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'roles', element: <RolesPage /> },
              // Step 163 — agents and groups on one page; `/admin/groups` stays as a
              // thin wrapper so old links still land.
              { path: 'agents', element: <AgentsPage /> },
              { path: 'service-desk', element: <ServiceDeskPage /> },
              { path: 'requesters', element: <RequestersPage /> },
              { path: 'freeze-windows', element: <FreezeWindowsPage /> },
              { path: 'approval-boards', element: <ApprovalBoardsPage /> },
              { path: 'sso', element: <SsoSettingsPage /> },
              { path: 'groups', element: <GroupsPage /> },
              { path: 'requester-groups', element: <RequesterGroupsPage /> },
              { path: 'audit', element: <AuditLogPage /> },
              { path: 'email-log', element: <EmailLogPage /> },
              { path: 'canned-responses', element: <CannedResponsesPage /> },
              { path: 'fields', element: <FieldLibraryPage /> },
              { path: 'catalogue', element: <CatalogueAdminPage /> },
              // Step 31 — the two settings pages the index already advertises.
              { path: 'business-hours', element: <BusinessHoursPage /> },
              { path: 'sla-policies', element: <SlaPoliciesPage /> },
              { path: 'automations', element: <AutomationsPage /> },
              { path: 'directory', element: <DirectorySyncPage /> },
              { path: 'ticket-templates', element: <TicketTemplatesPage /> },
              { path: 'saved-views', element: <SavedViewsPage /> },
              { path: 'approvals', element: <ApprovalsAdminPage /> },
              { path: 'api-keys', element: <ApiKeysPage /> },
            ],
          },
          {
            path: '/catalogue',
            element: <CataloguePage />,
          },
          { path: '/kb', element: <KbPage /> },
          { path: '/reports', element: <ReportsPage /> },
          { path: '/approvals', element: <ApprovalsPage /> },
          { path: '/announcements', element: <AnnouncementsPage /> },
          // Steps 53/130 — ONE page for requesters and agents (Kai: "the same
          // for agents… one template, just with more agent info").
          { path: '/people/:id', element: <PersonPage /> },
          // Step 48 — problems get their own list, on the SAME queue
          // component with the type pinned. Kai: "problems and changes
          // should be in a separate tab."
          { path: '/problems', element: <TicketsPage lockedType="problem" title="Problems" /> },
          { path: '/changes', element: <TicketsPage lockedType="change" title="Changes" /> },
          // Step 48/49 — a problem lives at /problems/:id, not /tickets/:id
          // (Kai). The detail page canonicalises the address itself, so old
          // links and anything already sent by email still land and then
          // correct themselves.
          { path: '/major-incidents/new', element: <NewTicketPage lockedType="major_incident" /> },
          { path: '/problems/new', element: <NewTicketPage lockedType="problem" /> },
          { path: '/changes/new', element: <NewTicketPage lockedType="change" /> },
          { path: '/problems/:id', element: <TicketDetailPage /> },
          { path: '/problems/:id/:tab', element: <TicketDetailPage /> },
          { path: '/changes/:id', element: <TicketDetailPage /> },
          { path: '/changes/:id/:tab', element: <TicketDetailPage /> },
          { path: '/changes/calendar', element: <ChangeCalendarPage /> },
          // Step 174 — Kai: "agents should be able to click their profile."
          { path: '/profile', element: <ProfilePage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

/** Headless — keeps the tab/home-screen icon in step with branding (Step 184). */
function BrandingHead() {
  useFavicon();
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrandingHead />
          <RouterProvider router={router} />
          <Toaster richColors position="bottom-right" theme="system" />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
